import { useEffect, useState } from 'react';
import db from '@/db';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { initFirebaseSync, subscribeSyncState, pingFirebase, teardownFirebaseSync } from '@/lib/firebaseSync';
import { invalidatePixCache } from '@/lib/mercadoPagoPix';
import { sendWhatsAppText, invalidateZapiCache } from '@/lib/zapiWhatsApp';
import { loadServerConfig, saveServerConfig } from '@/lib/serverConfig';
import { authedFetch } from '@/lib/adminAuth';
import { initializeApp, deleteApp } from 'firebase/app';
import { getDatabase, ref, set as rtdbSet, get as rtdbGet } from 'firebase/database';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { Flame, Zap, Send, CheckCircle2, AlertTriangle, Loader2, RefreshCw, Wifi, WifiOff, Beaker, ShieldCheck, Lock, RotateCw, ClipboardCopy, Download } from 'lucide-react';

// v7.17 — Mirror das chaves NÃO-sensíveis do Firebase no Dexie local.
// O `initFirebaseSync` ainda lê do `db.config`, então depois de carregar
// do servidor precisamos espelhar para que o boot mantenha as credenciais.
const FIREBASE_PUBLIC_KEYS = [
  'firebaseApiKey', 'firebaseAuthDomain', 'firebaseDatabaseURL',
  'firebaseProjectId', 'firebaseStorageBucket', 'firebaseMessagingSenderId',
  'firebaseAppId', 'deviceGroup',
];

async function mirrorPublicConfigToDexie(config) {
  if (!config || typeof config !== 'object') return;
  const ops = [];
  for (const k of FIREBASE_PUBLIC_KEYS) {
    if (k in config) ops.push({ key: k, value: config[k] });
  }
  if (ops.length) {
    try { await db.config.bulkPut(ops); } catch {/* cache opcional */}
  }
}

export default function IntegrationsCard() {
  // Estados de Firebase (TODAS chaves NÃO-sensíveis; voltam normalmente do GET).
  const [firebase, setFirebase] = useState({
    firebaseEnabled: false, firebaseApiKey: '', firebaseDatabaseURL: '', firebaseProjectId: '', firebaseAuthDomain: '', deviceGroup: 'kaven-default',
  });
  // Mercado Pago — `mpAccessToken` é sensível: começa VAZIO e exibimos mask.
  const [mp, setMp] = useState({ mpAccessToken: '', mpEnvironment: 'sandbox' });
  // Z-API — `zapiInstanceId` público, `zapiInstanceToken`/`zapiClientToken` sensíveis.
  const [zapi, setZapi] = useState({ zapiInstanceId: '', zapiInstanceToken: '', zapiClientToken: '' });
  // Flags do backend para saber se já existe valor sensível salvo.
  const [flags, setFlags] = useState({});
  // v7.19 — envLocked: mapa { key: true } indicando que a chave está vindo
  // de Deployment Secret (env do pod) — UI desabilita edição.
  const [envLocked, setEnvLocked] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  // Loading do GET inicial.
  const [loading, setLoading] = useState(true);

  const [syncState, setSyncState] = useState({ connected: false, initialized: false });
  const [pixReady, setPixReady] = useState(false);
  const [zapiReady, setZapiReady] = useState(false);
  const [busy, setBusy] = useState(null); // 'fb' | 'mp' | 'zapi' | null
  const [testingZapi, setTestingZapi] = useState(false);
  const [zapiTestPhone, setZapiTestPhone] = useState('');
  const [testingFirebase, setTestingFirebase] = useState(false);
  const [firebaseTestResult, setFirebaseTestResult] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const body = await loadServerConfig();
      const cfg = body?.config || {};
      const fl = body?.flags || {};
      const locked = body?.envLocked || {};
      setFlags(fl);
      setEnvLocked(locked);
      setUpdatedAt(body?.updatedAt || null);

      // Firebase (PÚBLICO — recebe valores)
      // `firebaseEnabled` é per-device, lido do Dexie local.
      const localEnabled = (await db.config.get('firebaseEnabled'))?.value === true;
      setFirebase({
        firebaseEnabled: localEnabled,
        firebaseApiKey: cfg.firebaseApiKey || '',
        firebaseDatabaseURL: cfg.firebaseDatabaseURL || '',
        firebaseProjectId: cfg.firebaseProjectId || '',
        firebaseAuthDomain: cfg.firebaseAuthDomain || '',
        deviceGroup: cfg.deviceGroup || 'kaven-default',
      });

      // Espelha as chaves públicas no Dexie pra `initFirebaseSync` continuar lendo.
      await mirrorPublicConfigToDexie(cfg);

      // Mercado Pago — token NUNCA volta; mostramos só mask se já configurado.
      setMp({
        mpAccessToken: '',
        mpEnvironment: cfg.mpEnvironment || 'sandbox',
      });

      // Z-API — instanceId é público; tokens viram mask.
      setZapi({
        zapiInstanceId: cfg.zapiInstanceId || '',
        zapiInstanceToken: '',
        zapiClientToken: '',
      });

      setPixReady(!!fl.hasMpAccessToken);
      setZapiReady(!!fl.hasZapiInstanceToken && !!cfg.zapiInstanceId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[IntegrationsCard] reload falhou:', err);
      toast.error('Não consegui buscar configurações do servidor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    const unsub = subscribeSyncState(setSyncState);
    return unsub;
  }, []);

  // ============ SAVE FIREBASE ============
  const saveFirebase = async () => {
    setBusy('fb');
    try {
      // Persist chaves PÚBLICAS no servidor (multi-device).
      await saveServerConfig({
        firebaseApiKey: firebase.firebaseApiKey.trim(),
        firebaseDatabaseURL: firebase.firebaseDatabaseURL.trim(),
        firebaseProjectId: firebase.firebaseProjectId.trim(),
        firebaseAuthDomain: firebase.firebaseAuthDomain.trim(),
        deviceGroup: (firebase.deviceGroup || 'kaven-default').trim(),
      });
      // Mirror no Dexie (initFirebaseSync lê de lá).
      await mirrorPublicConfigToDexie({
        firebaseApiKey: firebase.firebaseApiKey.trim(),
        firebaseDatabaseURL: firebase.firebaseDatabaseURL.trim(),
        firebaseProjectId: firebase.firebaseProjectId.trim(),
        firebaseAuthDomain: firebase.firebaseAuthDomain.trim(),
        deviceGroup: (firebase.deviceGroup || 'kaven-default').trim(),
      });
      // `firebaseEnabled` continua per-device (Dexie só).
      await db.config.put({ key: 'firebaseEnabled', value: !!firebase.firebaseEnabled });

      toast.success('Firebase salvo no servidor. Reconectando...');
      await initFirebaseSync();
      if (firebase.firebaseEnabled) {
        const ok = await pingFirebase(5000);
        if (ok) toast.success('✅ Firebase conectado (sync ativo)');
        else toast.error('Firebase respondeu mas conexão não estabilizou. Veja console.');
      }
    } catch (err) {
      toast.error('Falha ao salvar Firebase: ' + (err?.message || err));
    } finally {
      setBusy(null);
    }
  };

  const disableFirebase = async () => {
    await db.config.put({ key: 'firebaseEnabled', value: false });
    setFirebase((p) => ({ ...p, firebaseEnabled: false }));
    await teardownFirebaseSync();
    toast('Sync desativado neste dispositivo — rodando 100% local');
  };

  /**
   * Sanity check: valida credenciais Firebase sem mexer no app principal.
   * Cria uma app temporária, escreve + lê em /__healthcheck__/{ts}, destrói a app.
   */
  const runFirebaseSanityCheck = async () => {
    setTestingFirebase(true);
    setFirebaseTestResult(null);
    const tempName = `sanity-${Date.now()}`;
    let tempApp = null;
    try {
      if (!firebase.firebaseApiKey || !firebase.firebaseDatabaseURL || !firebase.firebaseProjectId) {
        throw new Error('Preencha apiKey + databaseURL + projectId antes do teste.');
      }
      tempApp = initializeApp({
        apiKey: firebase.firebaseApiKey.trim(),
        authDomain: firebase.firebaseAuthDomain.trim() || `${firebase.firebaseProjectId}.firebaseapp.com`,
        databaseURL: firebase.firebaseDatabaseURL.trim(),
        projectId: firebase.firebaseProjectId.trim(),
      }, tempName);

      const tempAuth = getAuth(tempApp);
      let uid = null;
      try {
        const cred = await signInAnonymously(tempAuth);
        uid = cred?.user?.uid || tempAuth.currentUser?.uid || null;
      } catch (authErr) {
        const code = authErr?.code || '';
        const msg = authErr?.message || String(authErr);
        let friendly = msg;
        if (code === 'auth/admin-restricted-operation' || code === 'auth/operation-not-allowed') {
          friendly = 'Provedor Anonymous DESABILITADO no Firebase. Vá em Authentication → Sign-in method → Anonymous → Habilitar.';
        } else if (code === 'auth/api-key-not-valid' || /invalid-api-key/i.test(code)) {
          friendly = 'apiKey inválida.';
        }
        throw new Error(friendly);
      }

      const rdb = getDatabase(tempApp);
      const group = (firebase.deviceGroup || 'kaven-default').trim();
      const path = `${group}/__healthcheck__/${uid || 'noauth'}-${Date.now()}`;
      await rtdbSet(ref(rdb, path), { at: new Date().toISOString(), from: 'kaven-sanity', uid });
      const snap = await rtdbGet(ref(rdb, path));
      if (!snap.exists()) throw new Error('Write succeeded but read returned empty.');
      setFirebaseTestResult({ ok: true, msg: `✓ Auth + Write + Read OK (uid: ${uid?.slice(0, 8) || 'n/a'}…) em ${path}` });
      toast.success('🔥 Firebase 100% operacional!');
    } catch (err) {
      const raw = String(err?.code || err?.message || err);
      let friendly = raw;
      if (raw.includes('auth/invalid-api-key') || raw.toLowerCase().includes('api-key-not-valid')) {
        friendly = 'apiKey inválida — Firebase recusou a chave.';
      } else if (raw.includes('PERMISSION_DENIED')) {
        friendly = 'Credenciais OK mas as Rules do Realtime DB bloqueiam escrita.';
      }
      setFirebaseTestResult({ ok: false, msg: friendly });
      toast.error(`Falha: ${friendly.slice(0, 90)}`);
    } finally {
      if (tempApp) {
        try { await deleteApp(tempApp); } catch {}
      }
      setTestingFirebase(false);
    }
  };

  const runLwwTest = async () => {
    if (!syncState.initialized) {
      toast.error('Ative o Firebase Sync primeiro (Salvar e Conectar).');
      return;
    }
    setTestingFirebase(true);
    setFirebaseTestResult(null);
    try {
      const testId = `lww-test-${Date.now()}`;
      await db.commands.add({
        number: 99999,
        status: 'open',
        items: [],
        buffetTotal: 0,
        itemsTotal: 0,
        total: 0,
        isTraining: true,
        _testTag: testId,
        createdAt: '2020-01-01T00:00:00.000Z',
        updatedAt: '2020-01-01T00:00:00.000Z',
      });
      await new Promise((r) => setTimeout(r, 500));
      const rows = await db.commands.filter((c) => c._testTag === testId).toArray();
      const target = rows[0];
      if (!target) throw new Error('Registro de teste não encontrado após insert');
      await db.commands.update(target.id, {
        items: [{ name: 'Teste LWW', quantity: 1, price: 1, subtotal: 1 }],
        itemsTotal: 1,
        total: 1,
        updatedAt: new Date().toISOString(),
      });
      await new Promise((r) => setTimeout(r, 1200));
      const after = await db.commands.get(target.id);
      await db.commands.delete(target.id);
      if (after && after.total === 1) {
        setFirebaseTestResult({
          ok: true,
          msg: `✓ LWW validado. Write antigo (2020) sobrescrito pelo write atual.`,
        });
        toast.success('✅ LWW OK — último write ganhou.');
      } else {
        throw new Error(`LWW inesperado: total=${after?.total}`);
      }
    } catch (err) {
      setFirebaseTestResult({ ok: false, msg: String(err?.message || err) });
      toast.error('LWW test falhou — veja detalhes no card.');
    } finally {
      setTestingFirebase(false);
    }
  };

  // ============ SAVE MP ============
  const saveMp = async () => {
    setBusy('mp');
    try {
      const payload = { mpEnvironment: mp.mpEnvironment };
      // Só envia o token sensível se o usuário digitou algo novo.
      if (mp.mpAccessToken.trim()) {
        payload.mpAccessToken = mp.mpAccessToken.trim();
      }
      await saveServerConfig(payload);
      invalidatePixCache();
      setMp((p) => ({ ...p, mpAccessToken: '' }));
      await reload();
      toast.success(mp.mpAccessToken.trim()
        ? `Access Token salvo no servidor (${mp.mpEnvironment})`
        : `Ambiente salvo (${mp.mpEnvironment})`);
    } catch (err) {
      toast.error('Falha ao salvar Mercado Pago: ' + (err?.message || err));
    } finally {
      setBusy(null);
    }
  };

  // ============ SAVE Z-API ============
  const saveZapi = async () => {
    setBusy('zapi');
    try {
      const payload = {};
      if (zapi.zapiInstanceId !== undefined) {
        payload.zapiInstanceId = zapi.zapiInstanceId.trim();
      }
      if (zapi.zapiInstanceToken.trim()) {
        payload.zapiInstanceToken = zapi.zapiInstanceToken.trim();
      }
      if (zapi.zapiClientToken.trim()) {
        payload.zapiClientToken = zapi.zapiClientToken.trim();
      }
      if (Object.keys(payload).length === 0) {
        toast.error('Nada para salvar — preencha algum campo.');
        return;
      }
      await saveServerConfig(payload);
      invalidateZapiCache();
      setZapi((p) => ({ ...p, zapiInstanceToken: '', zapiClientToken: '' }));
      await reload();
      toast.success('Z-API salvo no servidor');
    } catch (err) {
      toast.error('Falha ao salvar Z-API: ' + (err?.message || err));
    } finally {
      setBusy(null);
    }
  };

  const testZapi = async () => {
    if (!zapiTestPhone) return toast.error('Informe um telefone para teste');
    setTestingZapi(true);
    try {
      const res = await sendWhatsAppText({
        to: zapiTestPhone,
        message: '🧪 Teste Kaven — Z-API configurado com sucesso!',
        status: 'zapi_test',
        skipPersist: true,
      });
      if (res.sent) {
        toast.success('✓ Mensagem de teste enviada!', {
          description: `Para ${zapiTestPhone}. Confira o WhatsApp em segundos.`,
        });
      } else if (res.mocked) {
        toast('Modo MOCK — credenciais ausentes no servidor.', {
          description: 'Preencha Instance ID + Instance Token + salve antes de testar real.',
        });
      } else {
        toast.error('Z-API recusou o envio', {
          description: res.error || `HTTP ${res.status || '???'} sem detalhe.`,
          duration: 10000,
        });
      }
    } catch (err) {
      toast.error('Falha inesperada no teste Z-API', {
        description: String(err?.message || err),
        duration: 10000,
      });
    } finally {
      setTestingZapi(false);
    }
  };

  return (
    <section data-testid="integrations-card" className="bg-white border border-slate-200 rounded-2xl p-6 space-y-6">
      <header className="flex items-center gap-2 pb-3 border-b border-slate-100">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center">
          <Zap size={14} strokeWidth={2.25} />
        </div>
        <div className="flex-1">
          <p className="font-heading font-bold text-slate-900 text-base flex items-center gap-2">
            Integrações em Produção
            <span data-testid="server-managed-badge" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
              <ShieldCheck size={10} strokeWidth={2.5} /> Trancado no servidor
            </span>
          </p>
          <p className="text-[11px] text-slate-500">
            Tokens sensíveis (Z-API, Mercado Pago) ficam no backend Kaven. O navegador nunca recebe os valores brutos — apenas confirma se já foram configurados.
          </p>
          {updatedAt && (
            <p data-testid="config-updated-at" className="text-[10px] text-slate-400 mt-0.5">
              Última atualização: {new Date(updatedAt).toLocaleString('pt-BR')}
            </p>
          )}
        </div>
        <button
          data-testid="resync-config-btn"
          onClick={async () => {
            await reload();
            invalidatePixCache();
            invalidateZapiCache();
            toast.success('Configurações ressincronizadas do servidor');
          }}
          disabled={loading}
          title="Re-busca o estado atual das configurações no backend Kaven"
          className="shrink-0 px-3 h-9 rounded-lg border-2 border-indigo-200 text-indigo-700 text-xs font-bold hover:bg-indigo-50 disabled:opacity-50 flex items-center gap-1.5"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RotateCw size={13} />}
          Sincronizar
        </button>
      </header>

      {loading && (
        <div data-testid="integrations-loading" className="flex items-center gap-2 text-slate-500 text-sm py-4">
          <Loader2 size={14} className="animate-spin" /> Buscando configurações do servidor...
        </div>
      )}

      {/* ============ FIREBASE ============ */}
      <div data-testid="firebase-config-section" className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame size={16} className="text-amber-600" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Firebase Realtime DB (Sync)</p>
          </div>
          <StatusBadge
            data-testid="firebase-status-badge"
            ok={syncState.connected}
            label={syncState.initialized
              ? (syncState.connected ? 'Conectado' : 'Desconectado')
              : (firebase.firebaseEnabled ? 'Inicializando...' : 'Desativado')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="API Key" testid="fb-api-key"
            value={firebase.firebaseApiKey}
            onChange={(v) => setFirebase((p) => ({ ...p, firebaseApiKey: v }))}
            placeholder="AIzaSy..."
            envLocked={!!envLocked.firebaseApiKey} />
          <Field label="Project ID" testid="fb-project-id"
            value={firebase.firebaseProjectId}
            onChange={(v) => setFirebase((p) => ({ ...p, firebaseProjectId: v }))}
            placeholder="kaven-restaurante"
            envLocked={!!envLocked.firebaseProjectId} />
          <Field label="Database URL" testid="fb-database-url"
            value={firebase.firebaseDatabaseURL}
            onChange={(v) => setFirebase((p) => ({ ...p, firebaseDatabaseURL: v }))}
            placeholder="https://kaven-default-rtdb.firebaseio.com"
            envLocked={!!envLocked.firebaseDatabaseURL} fullWidth />
          <Field label="Auth Domain (opcional)" testid="fb-auth-domain"
            value={firebase.firebaseAuthDomain}
            onChange={(v) => setFirebase((p) => ({ ...p, firebaseAuthDomain: v }))}
            placeholder="{projectId}.firebaseapp.com"
            envLocked={!!envLocked.firebaseAuthDomain} />
          <Field label="Device Group" testid="fb-device-group"
            value={firebase.deviceGroup}
            onChange={(v) => setFirebase((p) => ({ ...p, deviceGroup: v }))}
            placeholder="kaven-default"
            envLocked={!!envLocked.deviceGroup} />
        </div>

        <label className="flex items-center justify-between gap-3 bg-amber-50/40 border border-amber-200 rounded-xl px-4 py-3">
          <div>
            <p className="text-sm font-bold text-amber-900">Ativar sincronização multi-device <span className="text-[10px] uppercase tracking-wider text-amber-700">(per-device)</span></p>
            <p className="text-[11px] text-amber-800/80 mt-0.5">
              Toggle individual deste dispositivo. As chaves (API Key, Project ID, etc) são globais no servidor.
            </p>
          </div>
          <Switch
            data-testid="firebase-enabled-toggle"
            checked={firebase.firebaseEnabled}
            onCheckedChange={(v) => setFirebase((p) => ({ ...p, firebaseEnabled: v }))}
          />
        </label>

        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="save-firebase-btn"
            onClick={saveFirebase}
            disabled={busy === 'fb'}
            className="px-5 h-10 rounded-lg bg-amber-600 text-white text-sm font-bold hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
          >
            {busy === 'fb' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Salvar e Conectar
          </button>
          <button
            data-testid="test-firebase-sanity-btn"
            onClick={runFirebaseSanityCheck}
            disabled={testingFirebase}
            className="px-4 h-10 rounded-lg border-2 border-amber-300 text-amber-700 text-sm font-bold hover:bg-amber-50 disabled:opacity-50 flex items-center gap-2"
          >
            {testingFirebase ? <Loader2 size={14} className="animate-spin" /> : <Beaker size={14} />}
            Testar Credenciais
          </button>
          <button
            data-testid="test-lww-btn"
            onClick={runLwwTest}
            disabled={testingFirebase || !syncState.initialized}
            className="px-4 h-10 rounded-lg border-2 border-slate-200 text-slate-600 text-sm font-bold hover:border-slate-400 disabled:opacity-40 flex items-center gap-2"
          >
            <RefreshCw size={14} /> Testar LWW
          </button>
          {syncState.initialized && (
            <button
              data-testid="disable-firebase-btn"
              onClick={disableFirebase}
              className="px-4 h-10 rounded-lg border border-slate-300 text-sm font-bold text-slate-600 hover:bg-slate-50"
            >
              Desativar Sync
            </button>
          )}
        </div>

        {firebaseTestResult && (
          <div
            data-testid="firebase-test-result"
            className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border text-xs font-semibold ${
              firebaseTestResult.ok
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {firebaseTestResult.ok ? (
              <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            )}
            <span className="leading-relaxed">{firebaseTestResult.msg}</span>
          </div>
        )}

        {firebaseTestResult && !firebaseTestResult.ok
          && /regras|Rules|PERMISSION_DENIED|bloqueando/i.test(firebaseTestResult.msg) && (
          <div data-testid="firebase-rules-helper" className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 space-y-2">
            <p className="text-xs font-black text-amber-900">
              Cole estas regras no Firebase Console
            </p>
            <pre className="bg-slate-900 text-emerald-300 text-[11px] font-mono p-3 rounded-md overflow-auto leading-relaxed">{`{
  "rules": {
    ".read": "auth != null",
    ".write": "auth != null"
  }
}`}</pre>
            <button
              data-testid="copy-firebase-rules-btn"
              onClick={() => {
                navigator.clipboard.writeText(
                  '{\n  "rules": {\n    ".read": "auth != null",\n    ".write": "auth != null"\n  }\n}'
                ).then(() => toast.success('Regras copiadas — cole no Firebase Console'));
              }}
              className="w-full h-9 rounded-md bg-amber-600 text-white text-xs font-bold hover:bg-amber-700"
            >
              Copiar regras
            </button>
          </div>
        )}
      </div>

      <div className="border-t border-slate-100" />

      {/* ============ MERCADO PAGO ============ */}
      <div data-testid="mp-config-section" className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-cyan-600" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Mercado Pago (Pix)</p>
          </div>
          <StatusBadge data-testid="mp-status-badge" ok={pixReady} label={pixReady ? 'Configurado' : 'Usando MOCK'} />
        </div>

        <SensitiveField
          label="Access Token"
          testid="mp-access-token"
          value={mp.mpAccessToken}
          onChange={(v) => setMp((p) => ({ ...p, mpAccessToken: v }))}
          placeholder="APP_USR-... (TEST-... para sandbox)"
          configured={!!flags.hasMpAccessToken}
          envLocked={!!envLocked.mpAccessToken}
          fullWidth
        />

        <div className="flex items-center gap-3">
          <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600">Ambiente:</label>
          <div className="flex gap-2">
            {['sandbox', 'production'].map((env) => (
              <button
                key={env}
                data-testid={`mp-env-${env}`}
                onClick={() => setMp((p) => ({ ...p, mpEnvironment: env }))}
                className={`px-3 h-8 rounded-lg text-xs font-bold border-2 transition-all ${
                  mp.mpEnvironment === env
                    ? env === 'production' ? 'bg-emerald-600 border-emerald-700 text-white' : 'bg-amber-500 border-amber-600 text-white'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'
                }`}
              >
                {env === 'production' ? 'Produção' : 'Sandbox'}
              </button>
            ))}
          </div>
        </div>

        <button
          data-testid="save-mp-btn"
          onClick={saveMp}
          disabled={busy === 'mp'}
          className="px-5 h-10 rounded-lg bg-cyan-600 text-white text-sm font-bold hover:bg-cyan-700 disabled:opacity-50 flex items-center gap-2"
        >
          {busy === 'mp' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Salvar Credenciais
        </button>

        <p className="text-[11px] text-slate-500 leading-relaxed">
          Token disponível em <strong>Mercado Pago → Credenciais → Access Token</strong>. O backend Kaven faz o proxy das chamadas (createPix + polling), o navegador nunca recebe o token.
        </p>
      </div>

      <div className="border-t border-slate-100" />

      {/* ============ Z-API ============ */}
      <div data-testid="zapi-config-section" className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Send size={16} className="text-emerald-600" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Z-API (WhatsApp envio)</p>
          </div>
          <StatusBadge data-testid="zapi-status-badge" ok={zapiReady} label={zapiReady ? 'Configurado' : 'Usando MOCK'} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Instance ID" testid="zapi-instance-id"
            value={zapi.zapiInstanceId}
            onChange={(v) => setZapi((p) => ({ ...p, zapiInstanceId: v }))}
            placeholder="3B1F2C..."
            envLocked={!!envLocked.zapiInstanceId} />
          <SensitiveField
            label="Instance Token"
            testid="zapi-instance-token"
            value={zapi.zapiInstanceToken}
            onChange={(v) => setZapi((p) => ({ ...p, zapiInstanceToken: v }))}
            placeholder="AB12CD..."
            configured={!!flags.hasZapiInstanceToken}
            envLocked={!!envLocked.zapiInstanceToken}
          />
          <SensitiveField
            label="Client-Token (opcional, se security ativado)"
            testid="zapi-client-token"
            value={zapi.zapiClientToken}
            onChange={(v) => setZapi((p) => ({ ...p, zapiClientToken: v }))}
            placeholder="F3..."
            configured={!!flags.hasZapiClientToken}
            envLocked={!!envLocked.zapiClientToken}
            fullWidth
          />
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          <button
            data-testid="save-zapi-btn"
            onClick={saveZapi}
            disabled={busy === 'zapi'}
            className="px-5 h-10 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            {busy === 'zapi' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Salvar
          </button>
          <div className="flex gap-2 items-center">
            <Input
              data-testid="zapi-test-phone"
              value={zapiTestPhone}
              onChange={(e) => setZapiTestPhone(e.target.value)}
              placeholder="(21) 99999-0000"
              className="h-10 w-44 border-2 border-slate-200"
            />
            <button
              data-testid="zapi-test-btn"
              onClick={testZapi}
              disabled={testingZapi}
              className="px-4 h-10 rounded-lg border-2 border-emerald-300 text-emerald-700 text-sm font-bold hover:bg-emerald-50 disabled:opacity-50"
            >
              {testingZapi ? 'Enviando...' : 'Testar envio'}
            </button>
          </div>
        </div>

        <p className="text-[11px] text-slate-500 leading-relaxed">
          O backend Kaven encaminha cada envio para api.z-api.io usando o token armazenado em <code>app_config</code>. Sem credenciais no servidor, o envio cai em MOCK e fica logado em <code>db.notifications</code>.
        </p>
      </div>

      <div className="border-t border-slate-100" />

      {/* ============ DEPLOYMENT SECRETS ============ */}
      <DeploymentSecretsPanel envLocked={envLocked} />
    </section>
  );
}

/**
 * v7.19 — Painel informativo que lista TODAS as env vars suportadas como
 * Deployment Secrets. Para cada uma:
 *   - se está ATIVA no servidor (envLocked[chave]=true), mostra chip verde
 *   - se não, mostra chip cinza com a env var sugerida pra colar no painel
 * Ajuda o operador a configurar o Deployment uma vez e sair voando.
 *
 * v7.20 — Adicionado botão "Exportar template .env" que gera snippet pronto
 * pra colar no painel Emergent Deployment → Environment Variables.
 */
function DeploymentSecretsPanel({ envLocked }) {
  const SECRETS = [
    { label: 'Firebase API Key',          key: 'firebaseApiKey',            env: 'FIREBASE_API_KEY',             group: 'Firebase' },
    { label: 'Firebase Auth Domain',      key: 'firebaseAuthDomain',        env: 'FIREBASE_AUTH_DOMAIN',         group: 'Firebase' },
    { label: 'Firebase Database URL',     key: 'firebaseDatabaseURL',       env: 'FIREBASE_DATABASE_URL',        group: 'Firebase' },
    { label: 'Firebase Project ID',       key: 'firebaseProjectId',         env: 'FIREBASE_PROJECT_ID',          group: 'Firebase' },
    { label: 'Firebase Storage Bucket',   key: 'firebaseStorageBucket',     env: 'FIREBASE_STORAGE_BUCKET',      group: 'Firebase' },
    { label: 'Firebase Messaging Sender', key: 'firebaseMessagingSenderId', env: 'FIREBASE_MESSAGING_SENDER_ID', group: 'Firebase' },
    { label: 'Firebase App ID',           key: 'firebaseAppId',             env: 'FIREBASE_APP_ID',              group: 'Firebase' },
    { label: 'Firebase Device Group',     key: 'deviceGroup',               env: 'FIREBASE_DEVICE_GROUP',        group: 'Firebase' },
    { label: 'Z-API Instance ID',         key: 'zapiInstanceId',            env: 'ZAPI_INSTANCE_ID',             group: 'Z-API' },
    { label: 'Z-API Instance Token',      key: 'zapiInstanceToken',         env: 'ZAPI_INSTANCE_TOKEN',          group: 'Z-API' },
    { label: 'Z-API Client Token',        key: 'zapiClientToken',           env: 'ZAPI_CLIENT_TOKEN',            group: 'Z-API' },
    { label: 'Mercado Pago Access Token', key: 'mpAccessToken',             env: 'MP_ACCESS_TOKEN',              group: 'Mercado Pago' },
    { label: 'Mercado Pago Public Key',   key: 'mpPublicKey',               env: 'MP_PUBLIC_KEY',                group: 'Mercado Pago' },
    { label: 'Mercado Pago Environment',  key: 'mpEnvironment',             env: 'MP_ENVIRONMENT',               group: 'Mercado Pago' },
    { label: 'JWT Secret (Config Auth)',  key: 'configJwtSecret',           env: 'CONFIG_JWT_SECRET',            group: 'Auth', alwaysAdvised: true },
    { label: 'Admin PINs (vírgula)',      key: 'adminConfigPins',           env: 'ADMIN_CONFIG_PINS',            group: 'Auth', alwaysAdvised: true },
  ];
  const activeCount = SECRETS.filter((s) => envLocked[s.key]).length;
  const [showTemplate, setShowTemplate] = useState(false);

  return (
    <div data-testid="deployment-secrets-panel" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-indigo-600" />
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Deployment Secrets</p>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="deployment-secrets-count"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold bg-indigo-50 border-indigo-200 text-indigo-700"
          >
            {activeCount}/{SECRETS.length - 2} ativas no servidor
          </span>
          <button
            data-testid="export-env-template-btn"
            onClick={() => setShowTemplate(true)}
            title="Gera um .env template pronto pra colar no painel de Deploy"
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border-2 border-indigo-300 text-indigo-700 text-[11px] font-bold hover:bg-indigo-50 transition-colors"
          >
            <Download size={12} strokeWidth={2.5} />
            Exportar template .env
          </button>
        </div>
      </div>

      <div className="bg-indigo-50/40 border border-indigo-200 rounded-xl p-3 text-[11px] text-indigo-900/90 leading-relaxed">
        <strong>Por que mapear como Deployment Secret?</strong>
        <br />Variáveis preenchidas no painel de Deploy ganham prioridade sobre o Mongo e <strong>sobrevivem a deploys/restarts</strong> sem reconfigurar pela UI. Para cada chave abaixo, abra o painel <em>Deployment → Environment Variables</em> e cole a env var correspondente.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {SECRETS.map((s) => {
          const active = !!envLocked[s.key];
          return (
            <div
              key={s.env}
              data-testid={`secret-row-${s.env}`}
              className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border ${
                active
                  ? 'bg-emerald-50 border-emerald-200'
                  : (s.alwaysAdvised ? 'bg-amber-50/50 border-amber-200' : 'bg-white border-slate-200')
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-900 truncate">{s.label}</p>
                <code className="text-[10px] font-mono text-slate-500 truncate block">{s.env}</code>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
                active
                  ? 'bg-emerald-600 text-white'
                  : (s.alwaysAdvised ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-600')
              }`}>
                {active ? '✓ ENV' : (s.alwaysAdvised ? '⚠ recomendada' : 'opcional')}
              </span>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-slate-500 leading-relaxed">
        💡 No Emergent: <strong>Painel de Deploy → Environment Variables</strong>. As env <code>CONFIG_JWT_SECRET</code> e <code>ADMIN_CONFIG_PINS</code> são <strong>fortemente recomendadas</strong> para que os tokens de sessão admin não invalidem em cada restart.
      </p>

      <EnvTemplateDialog open={showTemplate} onOpenChange={setShowTemplate} secrets={SECRETS} envLocked={envLocked} />
    </div>
  );
}

/** v7.20 — Gera um snippet .env documentado com TODAS as 15 variáveis */
function buildEnvTemplate(secrets, envLocked) {
  const stamp = new Date().toISOString();
  const lines = [
    '# ============================================',
    '# Kaven Restaurante — Deployment Secrets',
    `# Gerado em ${stamp}`,
    '#',
    '# Cole estas linhas no painel Emergent Deployment →',
    '# Environment Variables. Cada variável já está nomeada',
    '# exatamente como o backend espera.',
    '#',
    '# Status atual de cada chave no servidor:',
    '#   ✓  = já configurada via env (manter)',
    '#   ⚠  = recomendada (configurar antes do deploy)',
    '#   .. = opcional (vazia funciona em MOCK)',
    '# ============================================',
    '',
  ];
  const groups = {};
  for (const s of secrets) {
    (groups[s.group] = groups[s.group] || []).push(s);
  }
  for (const [g, items] of Object.entries(groups)) {
    lines.push(`# ---------- ${g} ----------`);
    for (const it of items) {
      const status = envLocked[it.key] ? '✓' : (it.alwaysAdvised ? '⚠' : '..');
      lines.push(`# [${status}] ${it.label}`);
      lines.push(`${it.env}=`);
      lines.push('');
    }
  }
  lines.push('# ============================================');
  lines.push('# Após colar e aplicar, reinicie o pod e use o botão');
  lines.push('# "Sincronizar" no card de Integrações para refletir.');
  lines.push('# ============================================');
  return lines.join('\n');
}

function EnvTemplateDialog({ open, onOpenChange, secrets, envLocked }) {
  // v7.21 — Template é buscado do backend via endpoint protegido por JWT admin.
  // Isso permite incluir os valores REAIS (tokens sensíveis) sem expor no GET público.
  // Fallback: se a chamada falhar, monta versão vazia (placeholders) localmente.
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await authedFetch(
          `${API_URL}/api/config/export-template`,
          { method: 'GET' },
          { message: 'Digite o PIN admin para gerar o template .env preenchido com tokens reais.' },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        if (!cancelled) {
          setText(body.template || '');
          setGeneratedAt(body.generatedAt || null);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[ENV TEMPLATE] backend falhou, usando fallback local:', err);
        if (!cancelled) {
          setText(buildEnvTemplate(secrets, envLocked));
          setGeneratedAt(null);
          toast('Template gerado localmente (sem valores sensíveis)', {
            description: 'Login admin foi cancelado ou falhou. Os tokens reais não foram incluídos.',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Template .env copiado para o clipboard', {
        description: 'Cole no painel Emergent Deployment → Environment Variables',
      });
    } catch {
      toast.error('Não consegui copiar — selecione e copie manualmente');
    }
  };
  const download = () => {
    try {
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kaven-deployment-secrets-${new Date().toISOString().slice(0, 10)}.env`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Arquivo .env baixado');
    } catch (e) {
      toast.error('Falha ao baixar: ' + (e?.message || e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="env-template-dialog"
        className="max-w-2xl bg-white border-2 border-indigo-200 rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-bold text-slate-900">
            <Download size={18} className="text-indigo-600" />
            Template .env para deploy
            {loading && <Loader2 size={14} className="animate-spin text-indigo-500" />}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 leading-relaxed">
            Snippet pronto pra colar no painel <strong>Emergent Deployment → Environment Variables</strong>. {generatedAt && (
              <span data-testid="env-template-generated-at">Gerado pelo servidor em <code>{new Date(generatedAt).toLocaleString('pt-BR')}</code> — valores REAIS incluídos.</span>
            )}
          </DialogDescription>
        </DialogHeader>
        <pre
          data-testid="env-template-preview"
          className="bg-slate-900 text-emerald-300 text-[11px] font-mono p-3 rounded-md overflow-auto leading-relaxed max-h-[60vh]"
        >{loading ? 'Carregando do servidor...' : text}</pre>
        <div className="flex gap-2">
          <button
            data-testid="copy-env-template-btn"
            onClick={copy}
            disabled={loading || !text}
            className="flex-1 h-10 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <ClipboardCopy size={14} /> Copiar para clipboard
          </button>
          <button
            data-testid="download-env-template-btn"
            onClick={download}
            disabled={loading || !text}
            className="px-4 h-10 rounded-lg border-2 border-indigo-300 text-indigo-700 text-sm font-bold hover:bg-indigo-50 disabled:opacity-50 flex items-center gap-2"
          >
            <Download size={14} /> Baixar .env
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, testid, value, onChange, placeholder, fullWidth, type, envLocked }) {
  return (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-1 flex items-center gap-1.5">
        {label}
        {envLocked && (
          <span
            data-testid={`${testid}-env-badge`}
            className="inline-flex items-center gap-1 text-[9px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-1.5 py-px"
            title="Vindo de Deployment Secret — edição via UI desabilitada"
          >
            <Lock size={9} strokeWidth={2.5} /> ENV
          </span>
        )}
      </label>
      <Input
        data-testid={testid}
        type={type || 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={!!envLocked}
        className={`h-10 border-2 rounded-lg text-sm font-mono ${
          envLocked ? 'border-indigo-200 bg-indigo-50/30 cursor-not-allowed' : 'border-slate-200'
        }`}
      />
    </div>
  );
}

/**
 * Input para campo SENSÍVEL. Quando `configured=true`, mostra placeholder
 * mascarado indicando que já existe valor salvo no servidor; o input fica
 * vazio e só envia novo valor se o usuário digitar.
 */
function SensitiveField({ label, testid, value, onChange, placeholder, configured, fullWidth, envLocked }) {
  const maskPlaceholder = '••••••••••••  (configurado — digite para substituir)';
  const isLocked = !!envLocked;
  return (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-1 flex items-center gap-1.5">
        {label}
        {isLocked && (
          <span
            data-testid={`${testid}-env-badge`}
            className="inline-flex items-center gap-1 text-[9px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-1.5 py-px"
            title="Vindo de Deployment Secret"
          >
            <Lock size={9} strokeWidth={2.5} /> ENV
          </span>
        )}
        {!isLocked && configured && (
          <span
            data-testid={`${testid}-configured-badge`}
            className="inline-flex items-center gap-1 text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-px"
          >
            <Lock size={9} strokeWidth={2.5} /> Salvo
          </span>
        )}
      </label>
      <Input
        data-testid={testid}
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isLocked ? '••••••• (carregado via Deployment Secret)' : (configured ? maskPlaceholder : placeholder)}
        disabled={isLocked}
        className={`h-10 border-2 rounded-lg text-sm font-mono ${
          isLocked ? 'border-indigo-200 bg-indigo-50/30 cursor-not-allowed' :
          (configured ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200')
        }`}
        autoComplete="off"
      />
      {isLocked && (
        <p data-testid={`${testid}-env-hint`} className="text-[10px] text-indigo-700 mt-1">
          🔐 Vindo de Deployment Secret do servidor. Para alterar, atualize a env var no painel de Deploy.
        </p>
      )}
      {!isLocked && configured && (
        <p data-testid={`${testid}-mask-hint`} className="text-[10px] text-emerald-700 mt-1">
          🔒 Valor já está trancado no servidor. Deixe vazio para manter.
        </p>
      )}
    </div>
  );
}

function StatusBadge({ ok, label, 'data-testid': dti }) {
  const okCls = 'bg-emerald-50 border-emerald-200 text-emerald-700';
  const notCls = 'bg-slate-50 border-slate-200 text-slate-500';
  const Icon = ok ? Wifi : WifiOff;
  return (
    <span
      data-testid={dti}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold ${ok ? okCls : notCls}`}
    >
      <Icon size={11} strokeWidth={2.25} />
      {label}
    </span>
  );
}
